import { z } from 'zod';
import { valueListItemStatusSchema } from './enums.js';
/** Keys of the value lists that exist from day one and cannot be deleted. */
export const SYSTEM_VALUE_LIST_KEYS = [
    'suppliers',
    'buyers',
    'seasons',
    'themes',
    'projects',
];
export const systemValueListKeySchema = z.enum(SYSTEM_VALUE_LIST_KEYS);
export const valueListKeySchema = z
    .string()
    .min(1)
    .max(64)
    .regex(/^[a-z][a-z0-9_]*$/, 'Key must be lower snake_case and start with a letter');
export const valueListSchema = z.object({
    id: z.uuid(),
    key: valueListKeySchema,
    name: z.string().min(1).max(200),
    description: z.string().max(1000).nullable(),
    /** System lists cannot be deleted and their key is frozen. */
    system: z.boolean(),
    /**
     * Whether the iPhone app may propose new entries while offline. On for
     * suppliers — an unknown supplier at a booth must not block capture. Off for
     * seasons, where the set of valid values is decided centrally.
     */
    allowClientCreation: z.boolean(),
    createdAt: z.date(),
    updatedAt: z.date(),
    deletedAt: z.date().nullable(),
});
/**
 * Extra structured data on a supplier entry. Kept in the generic
 * `value_list_items.metadata` JSONB so suppliers need no special table, while
 * still being validated and searchable.
 */
export const supplierMetadataSchema = z.object({
    supplierNumber: z.string().max(64).nullable().default(null),
    country: z
        .string()
        .length(2)
        .regex(/^[A-Z]{2}$/, 'ISO 3166-1 alpha-2, uppercase')
        .nullable()
        .default(null),
    contact: z.string().max(500).nullable().default(null),
    note: z.string().max(2000).nullable().default(null),
});
/** Metadata schemas per system list. Lists without one accept any JSON object. */
export const VALUE_LIST_METADATA_SCHEMAS = {
    suppliers: supplierMetadataSchema,
};
export const valueListItemSchema = z.object({
    id: z.uuid(),
    valueListId: z.uuid(),
    /** What the user sees in the picker. */
    label: z.string().min(1).max(300),
    /**
     * Stable machine value used in exports. Defaults to the label when the list
     * has no separate coding.
     */
    value: z.string().min(1).max(300),
    metadata: z.record(z.string(), z.unknown()),
    status: valueListItemStatusSchema,
    /** Set when this entry was merged away; points at the surviving entry. */
    mergedIntoId: z.uuid().nullable(),
    sortOrder: z.number().int(),
    /** Who proposed this entry, when it came from the app. */
    createdByUserId: z.uuid().nullable(),
    createdAt: z.date(),
    updatedAt: z.date(),
    deletedAt: z.date().nullable(),
});
export const valueListItemCreateSchema = z.object({
    label: z.string().min(1).max(300),
    value: z.string().min(1).max(300).optional(),
    metadata: z.record(z.string(), z.unknown()).default({}),
    sortOrder: z.number().int().default(0),
});
/**
 * Merging a duplicate into a survivor. Items referencing the loser are
 * repointed, and the loser stays as a tombstone so offline clients holding the
 * old id can still resolve it.
 */
export const valueListItemMergeSchema = z.object({
    sourceId: z.uuid(),
    targetId: z.uuid(),
});
//# sourceMappingURL=value-list.js.map